// Generic JSON walker used by `XdrValue.toJson()` / `XdrValue.fromJson()`.
//
// The walker dispatches on `schema.kind` to convert between the raw "wire"
// shape (returned by `toXdrObject()`) and a SEP-0051-style JSON value.
// Schema-name overrides cover types that need a custom encoding (StrKey
// addresses, wide-int parts collapsed to a decimal string, etc.); types
// without an override fall through to the kind-based default.

import { uint8ArrayToHex, hexToUint8Array } from "uint8array-extras";

import { XdrError } from "@stellar/js-xdr";
import type {
  AnySchema,
  EnumSchema,
  Field,
  UnionArm,
  UnionSchema,
  XdrType,
} from "@stellar/js-xdr";
import type { JsonValue } from "./xdr-value.js";
import { XdrString } from "./xdr-string.js";
import {
  acceptedStructJsonKeys,
  legacyStructFieldJsonName,
  structFieldJsonName,
  enumJsonNames,
  unionCaseNames,
} from "./json-names.js";
import {
  assertBigIntFits,
  assertDecimalDigitBudget,
  assertDecimalString,
  bigIntTo128Parts,
  bigIntTo256Parts,
  partsTo128BigInt,
  partsTo256BigInt,
  type Int128Parts,
  type Int256Parts,
} from "./bigint-parts.js";
import {
  StrKey,
  packMuxedContract,
  unpackMuxedContract,
} from "../../base/strkey.js";
import type { ScAddressWire } from "../generated/sc-address.js";
import type { MuxedContractWire } from "../generated/muxed-contract.js";

// The enum's camelized `member_prefix`, stripped before snake_casing to
// recover the SEP-0051 JSON name. `memberPrefix` is attached by
// `withMemberPrefix` in this values layer — it is not a js-xdr concept, so it
// is layered onto the exported `EnumSchema` here.
type EnumWithPrefix = EnumSchema<string, Record<string, number>> & {
  readonly memberPrefix?: string;
};

interface JsonOverride {
  toJson(wire: unknown): JsonValue;
  fromJson(json: JsonValue): unknown;
}

const OVERRIDES = new Map<string, JsonOverride>();

export function walkToJson(wire: unknown, schema: XdrType<unknown>): JsonValue {
  // The override lookup runs before the AnySchema cast: overrides also cover
  // SDK-custom schema kinds that are outside js-xdr's closed union.
  const override = schema.name ? OVERRIDES.get(schema.name) : undefined;
  if (override) return override.toJson(wire);

  const s = schema as AnySchema;
  switch (s.kind) {
    case "bool":
      return wire as boolean;
    case "void":
      return null;
    case "int32":
    case "uint32":
      return wire as number;
    case "float":
    case "double":
      return wire as number;
    case "int64":
    case "uint64":
      return (wire as bigint).toString();
    case "string":
      return (wire as XdrString).toJson();
    case "opaque":
    case "varOpaque":
      // SEP-0051: both fixed and variable opaque are hex-encoded strings.
      return uint8ArrayToHex(wire as Uint8Array);
    case "enum": {
      const e = s as EnumWithPrefix;
      const name = e.nameByValue.get(wire as number);
      if (name === undefined) {
        throw new XdrError(
          `${schema.name ?? "enum"}: unknown enum value ${String(wire)}`,
        );
      }
      return (
        enumJsonNames(e.memberPrefix, e.nameByValue).bySource.get(name) ?? name
      );
    }
    case "option":
      return wire === null ? null : walkToJson(wire, s.element);
    case "array":
    case "fixedArray":
      return (wire as unknown[]).map((v) => walkToJson(v, s.element));
    case "struct": {
      const rec = wire as Record<string, unknown>;
      const out: Record<string, JsonValue> = {};
      // SEP-0051: struct field keys are snake_case in JSON. Codegen produces
      // camelCase wire field names (`assetCode`), so transform on emit.
      for (const [k, fieldSchema] of s.entries) {
        out[structFieldJsonName(k)] = walkToJson(rec[k], fieldSchema);
      }
      return out;
    }
    case "union": {
      const rec = wire as Record<string, unknown>;
      const disc = rec[s.switchKey];
      const matched = s.cases.find((c) => c.discriminant === disc);
      if (matched === undefined) {
        // A defaulted case would have to emit a lossy "default" key that
        // fromJson can't reverse. No Stellar schema declares a default arm;
        // fail loudly if one ever appears instead of corrupting JSON.
        if (s.defaultArm !== undefined) {
          throw new XdrError(
            `${schema.name ?? "union"}: default arms are not supported in JSON`,
          );
        }
        throw new XdrError(
          `${schema.name ?? "union"}: no case for discriminator ${String(disc)}`,
        );
      }
      const arm = matched.arm;
      const names = unionCaseNames(s);
      const jsonKey = names.bySource.get(matched.name) ?? matched.name;
      if (isFieldArm(arm)) {
        return {
          [jsonKey]: walkToJson(rec[arm.name], arm.schema),
        };
      }
      return jsonKey;
    }
    case "lazy":
      return walkToJson(wire, s.getSchema());
    default:
      throw new XdrError(
        `${schema.name ?? schema.kind}: toJson unsupported for kind ${schema.kind}`,
      );
  }
}

export function walkFromJson(
  json: JsonValue,
  schema: XdrType<unknown>,
): unknown {
  // Override lookup first, as in `walkToJson` — see the note there.
  const override = schema.name ? OVERRIDES.get(schema.name) : undefined;
  if (override) return override.fromJson(json);

  const s = schema as AnySchema;
  switch (s.kind) {
    case "bool":
      assertJsonType(json, "boolean", schema);
      return json;
    case "void":
      return undefined;
    case "int32":
    case "uint32": {
      assertJsonType(json, "number", schema);
      const value = json as number;
      const signed = s.kind === "int32";
      const min = signed ? -(2 ** 31) : 0;
      const max = signed ? 2 ** 31 - 1 : 2 ** 32 - 1;
      if (!Number.isInteger(value) || value < min || value > max) {
        throw new XdrError(
          `${schema.name ?? s.kind}: value ${value} is not a valid ${s.kind}`,
        );
      }
      return value;
    }
    case "float":
    case "double":
      assertJsonType(json, "number", schema);
      return json;
    case "int64":
    case "uint64": {
      assertJsonType(json, "string", schema);
      assertDecimalDigitBudget(json as string, 64, schema.name ?? s.kind);
      assertDecimalString(json as string, schema.name ?? s.kind);
      let value: bigint;
      try {
        value = BigInt(json as string);
      } catch {
        throw new XdrError(
          `${schema.name ?? s.kind}: "${json as string}" is not a valid ${s.kind}`,
        );
      }
      assertBigIntFits(value, s.kind === "int64", 64, schema.name ?? s.kind);
      return value;
    }
    case "string": {
      assertJsonType(json, "string", schema);
      const str = XdrString.fromJson(json as string);
      if (str.length > s.maxLength) {
        throw new XdrError(
          `${schema.name ?? "string"}: length ${str.length} exceeds max ${s.maxLength}`,
        );
      }
      return str;
    }
    case "opaque":
    case "varOpaque": {
      assertJsonType(json, "string", schema);
      let bytes: Uint8Array;
      try {
        bytes = hexToUint8Array(json as string);
      } catch {
        throw new XdrError(`${schema.name ?? s.kind}: expected a hex string`);
      }
      if (s.kind === "opaque" && bytes.length !== s.length) {
        throw new XdrError(
          `${schema.name ?? "opaque"}: expected ${s.length} byte(s), got ${bytes.length}`,
        );
      }
      if (s.kind === "varOpaque" && bytes.length > s.maxLength) {
        throw new XdrError(
          `${schema.name ?? "varOpaque"}: length ${bytes.length} exceeds max ${s.maxLength}`,
        );
      }
      return bytes;
    }
    case "enum": {
      const e = s as EnumWithPrefix;
      assertJsonType(json, "string", schema);
      const target = json as string;
      const names = enumJsonNames(e.memberPrefix, e.nameByValue);
      const sourceName = names.byJson.get(target);
      if (sourceName !== undefined) {
        for (const [value, name] of e.nameByValue) {
          if (name === sourceName) return value;
        }
      }
      throw new XdrError(
        `${schema.name ?? "enum"}: unknown enum name ${target}`,
      );
    }
    case "option":
      return json === null ? null : walkFromJson(json, s.element);
    case "array": {
      if (!Array.isArray(json)) {
        throw new XdrError(
          `${schema.name ?? "array"}: expected JSON array, got ${typeof json}`,
        );
      }
      if (json.length > s.maxLength) {
        throw new XdrError(
          `${schema.name ?? "array"}: length ${json.length} exceeds max ${s.maxLength}`,
        );
      }
      return json.map((v) => walkFromJson(v, s.element));
    }
    case "fixedArray": {
      if (!Array.isArray(json)) {
        throw new XdrError(
          `${schema.name ?? "fixedArray"}: expected JSON array, got ${typeof json}`,
        );
      }
      if (json.length !== s.length) {
        throw new XdrError(
          `${schema.name ?? "fixedArray"}: expected length ${s.length}, got ${json.length}`,
        );
      }
      return json.map((v) => walkFromJson(v, s.element));
    }
    case "struct": {
      if (!isPlainObject(json)) {
        throw new XdrError(`${schema.name ?? "struct"}: expected JSON object`);
      }
      const rec = json as Record<string, JsonValue>;
      const out: Record<string, unknown> = {};
      // Accept the SEP-0051 snake_case key or the legacy Rust keyword-escaped
      // key (`type_` from the `stellar-xdr` crate's buggy output). Supplying
      // both spellings of the same field is ambiguous and rejected. Any other
      // key is rejected outright: an absent optional field maps to `null`, so
      // a silently ignored typo'd key would otherwise drop data undetectably.
      const accepted = acceptedStructJsonKeys(s.entries);
      for (const key of Object.keys(rec)) {
        if (!accepted.has(key)) {
          throw new XdrError(
            `${schema.name ?? "struct"}: unknown field ${key}`,
          );
        }
      }
      for (const [k, fieldSchema] of s.entries) {
        const snake = structFieldJsonName(k);
        const legacy = legacyStructFieldJsonName(k);
        if (
          legacy !== undefined &&
          Object.hasOwn(rec, snake) &&
          Object.hasOwn(rec, legacy)
        ) {
          throw new XdrError(
            `${schema.name ?? "struct"}: field ${k} given as both ` +
              `${snake} and ${legacy}`,
          );
        }
        const lookupKey = Object.hasOwn(rec, snake)
          ? snake
          : legacy !== undefined && Object.hasOwn(rec, legacy)
            ? legacy
            : undefined;
        if (lookupKey === undefined) {
          // Match the serde-based Rust reference: an omitted optional field
          // is None, so JSON from tooling that skips nulls still parses.
          if (unwrapLazy(fieldSchema).kind === "option") {
            out[k] = null;
            continue;
          }
          throw new XdrError(
            `${schema.name ?? "struct"}: missing field ${snake}`,
          );
        }
        out[k] = walkFromJson(rec[lookupKey], fieldSchema);
      }
      return out;
    }
    case "union":
      return unionFromJson(json, s);
    case "lazy":
      return walkFromJson(json, s.getSchema());
    default:
      throw new XdrError(
        `${schema.name ?? schema.kind}: fromJson unsupported for kind ${schema.kind}`,
      );
  }
}

function unionFromJson(json: JsonValue, schema: UnionSchema<unknown>): unknown {
  let jsonKey: string;
  let payload: JsonValue | undefined;
  if (typeof json === "string") {
    jsonKey = json;
  } else if (isPlainObject(json)) {
    const keys = Object.keys(json as object);
    if (keys.length !== 1) {
      throw new XdrError(
        `${schema.name ?? "union"}: expected single-key object, got ${keys.length}`,
      );
    }
    jsonKey = keys[0];
    payload = (json as Record<string, JsonValue>)[jsonKey];
  } else {
    throw new XdrError(
      `${schema.name ?? "union"}: expected string or single-key object`,
    );
  }

  // Only the SEP-0051 form (snake_case, prefix stripped) is accepted.
  const names = unionCaseNames(schema);
  const sourceName = names.byJson.get(jsonKey);
  const matched =
    sourceName === undefined
      ? undefined
      : schema.cases.find((c) => c.name === sourceName);
  if (matched === undefined) {
    // See the walkToJson union case: default arms are deliberately
    // unsupported — the "default" JSON key would discard the discriminant.
    if (schema.defaultArm !== undefined) {
      throw new XdrError(
        `${schema.name ?? "union"}: default arms are not supported in JSON`,
      );
    }
    throw new XdrError(`${schema.name ?? "union"}: unknown case ${jsonKey}`);
  }
  const arm = matched.arm;
  const discriminant = matched.discriminant;
  const out: Record<string, unknown> = { [schema.switchKey]: discriminant };
  if (isFieldArm(arm)) {
    if (payload === undefined) {
      throw new XdrError(
        `${schema.name ?? "union"}.${jsonKey}: missing arm payload`,
      );
    }
    out[arm.name] = walkFromJson(payload, arm.schema);
  } else if (payload !== undefined && payload !== null) {
    throw new XdrError(
      `${schema.name ?? "union"}.${jsonKey}: unexpected payload for void arm`,
    );
  }
  return out;
}

function isFieldArm(arm: UnionArm): arm is Field<string, XdrType<unknown>> {
  return typeof arm === "object" && arm !== null && "schema" in arm;
}

function unwrapLazy(schema: XdrType<unknown>): AnySchema {
  let s = schema as AnySchema;
  while (s.kind === "lazy") {
    s = s.getSchema() as AnySchema;
  }
  return s;
}

function assertJsonType(
  json: JsonValue,
  expected: "string" | "number" | "boolean",
  schema: XdrType<unknown>,
): void {
  if (typeof json !== expected) {
    throw new XdrError(
      `${schema.name ?? schema.kind}: expected JSON ${expected}, got ${typeof json}`,
    );
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Uint8Array)
  );
}

function asDataView(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

// ---------- overrides ----------
// StrKey-encoded addresses.

// Shared wire ↔ strkey codecs for shapes that appear in several overrides.

interface Med25519Wire {
  id: bigint;
  ed25519: Uint8Array;
}

function med25519ToStrkey(w: Med25519Wire): string {
  assertBigIntFits(w.id, false, 64, "MuxedEd25519Account.id");
  const payload = new Uint8Array(40);
  payload.set(w.ed25519, 0);
  asDataView(payload).setBigUint64(32, w.id);
  return StrKey.encodeMed25519PublicKey(payload);
}

function med25519FromStrkey(json: string): Med25519Wire {
  const raw = StrKey.decodeMed25519PublicKey(json);
  return {
    id: asDataView(raw).getBigUint64(32),
    ed25519: raw.subarray(0, 32),
  };
}

function muxedContractToStrkey(w: MuxedContractWire): string {
  return StrKey.encodeMuxedContract(packMuxedContract(w.contractId, w.id));
}

function muxedContractFromStrkey(json: string): MuxedContractWire {
  return unpackMuxedContract(StrKey.decodeMuxedContract(json));
}

interface SignedPayloadWire {
  ed25519: Uint8Array;
  payload: Uint8Array;
}

function signedPayloadToStrkey(w: SignedPayloadWire): string {
  const buf = new Uint8Array(32 + 4 + roundUp4(w.payload.length));
  buf.set(w.ed25519, 0);
  asDataView(buf).setUint32(32, w.payload.length);
  buf.set(w.payload, 36);
  return StrKey.encodeSignedPayload(buf);
}

function signedPayloadFromStrkey(json: string): SignedPayloadWire {
  const raw = StrKey.decodeSignedPayload(json);
  return {
    ed25519: raw.subarray(0, 32),
    payload: raw.subarray(36, 36 + asDataView(raw).getUint32(32)),
  };
}

OVERRIDES.set("PublicKey", {
  toJson(wire) {
    const w = wire as { type: number; ed25519: Uint8Array };
    if (w.type !== 0) {
      throw new XdrError(
        `PublicKey: unsupported variant ${w.type} for JSON output`,
      );
    }
    return StrKey.encodeEd25519PublicKey(w.ed25519);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("PublicKey: expected G-strkey string");
    }
    return {
      type: 0,
      ed25519: StrKey.decodeEd25519PublicKey(json),
    };
  },
});

OVERRIDES.set("MuxedAccount", {
  toJson(wire) {
    const w = wire as
      | { type: 0; ed25519: Uint8Array }
      | { type: 256; med25519: { id: bigint; ed25519: Uint8Array } };
    if (w.type === 0) {
      return StrKey.encodeEd25519PublicKey(w.ed25519);
    }
    return med25519ToStrkey(w.med25519);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("MuxedAccount: expected strkey string");
    }
    if (json.startsWith("G")) {
      return {
        type: 0,
        ed25519: StrKey.decodeEd25519PublicKey(json),
      };
    }
    if (json.startsWith("M")) {
      return { type: 256, med25519: med25519FromStrkey(json) };
    }
    throw new XdrError(`MuxedAccount: unsupported strkey prefix in ${json}`);
  },
});

// Standalone med25519-shaped structs render as an M-address in the reference,
// so each named schema gets the shared codec as its own override. This covers
// `MuxedEd25519Account` and the `MuxedAccount.med25519` arm struct
// (`MuxedAccountMed25519`), which is normally rendered through the
// `MuxedAccount` override above but also string-encodes when standalone.
const MED25519_OVERRIDE: JsonOverride = {
  toJson(wire) {
    return med25519ToStrkey(wire as Med25519Wire);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("med25519 account: expected M-strkey string");
    }
    return med25519FromStrkey(json);
  },
};

OVERRIDES.set("MuxedEd25519Account", MED25519_OVERRIDE);
OVERRIDES.set("MuxedAccountMed25519", MED25519_OVERRIDE);

// A standalone muxed contract renders as a W-address in the reference.
OVERRIDES.set("MuxedContract", {
  toJson(wire) {
    return muxedContractToStrkey(wire as MuxedContractWire);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("MuxedContract: expected W-strkey string");
    }
    return muxedContractFromStrkey(json);
  },
});

OVERRIDES.set("SignerKey", {
  toJson(wire) {
    const w = wire as
      | { type: 0; ed25519: Uint8Array }
      | { type: 1; preAuthTx: Uint8Array }
      | { type: 2; hashX: Uint8Array }
      | {
          type: 3;
          ed25519SignedPayload: { ed25519: Uint8Array; payload: Uint8Array };
        };
    switch (w.type) {
      case 0:
        return StrKey.encodeEd25519PublicKey(w.ed25519);
      case 1:
        return StrKey.encodePreAuthTx(w.preAuthTx);
      case 2:
        return StrKey.encodeSha256Hash(w.hashX);
      case 3:
        return signedPayloadToStrkey(w.ed25519SignedPayload);
    }
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("SignerKey: expected strkey string");
    }
    if (json.startsWith("G")) {
      return {
        type: 0,
        ed25519: StrKey.decodeEd25519PublicKey(json),
      };
    }
    if (json.startsWith("T")) {
      return {
        type: 1,
        preAuthTx: StrKey.decodePreAuthTx(json),
      };
    }
    if (json.startsWith("X")) {
      return { type: 2, hashX: StrKey.decodeSha256Hash(json) };
    }
    if (json.startsWith("P")) {
      return { type: 3, ed25519SignedPayload: signedPayloadFromStrkey(json) };
    }
    throw new XdrError(`SignerKey: unsupported strkey prefix in ${json}`);
  },
});

// The `SignerKey.ed25519SignedPayload` arm struct. Like the med25519 case
// above, the reference string-encodes it (as a P-strkey) when serialized
// standalone, so it needs its own override in addition to the `SignerKey` one.
OVERRIDES.set("SignerKeyEd25519SignedPayload", {
  toJson(wire) {
    return signedPayloadToStrkey(wire as SignedPayloadWire);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError(
        "SignerKeyEd25519SignedPayload: expected P-strkey string",
      );
    }
    return signedPayloadFromStrkey(json);
  },
});

OVERRIDES.set("ScAddress", {
  toJson(wire) {
    // Typed against the generated wire so a new arm fails to compile here
    // instead of serializing to `undefined`.
    const w = wire as ScAddressWire;
    switch (w.type) {
      case 0:
        return StrKey.encodeEd25519PublicKey(w.accountId.ed25519);
      case 1:
        return StrKey.encodeContract(w.contractId);
      case 2:
        return med25519ToStrkey(w.muxedAccount);
      case 3: {
        const cb = w.claimableBalanceId;
        // CLAIMABLE_BALANCE_ID_TYPE_V0 (=0) prefix-byte (zero-initialized)
        const raw = new Uint8Array(1 + 32);
        raw.set(cb.v0, 1);
        return StrKey.encodeClaimableBalance(raw);
      }
      case 4:
        return StrKey.encodeLiquidityPool(w.liquidityPoolId);
      case 5:
        return muxedContractToStrkey(w.muxedContract);
      default: {
        const unknown: never = w;
        throw new XdrError(
          `ScAddress: unsupported type ${(unknown as { type: number }).type}`,
        );
      }
    }
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("ScAddress: expected strkey string");
    }
    if (json.startsWith("G")) {
      return {
        type: 0,
        accountId: {
          type: 0,
          ed25519: StrKey.decodeEd25519PublicKey(json),
        },
      };
    }
    if (json.startsWith("C")) {
      return { type: 1, contractId: StrKey.decodeContract(json) };
    }
    if (json.startsWith("M")) {
      return { type: 2, muxedAccount: med25519FromStrkey(json) };
    }
    if (json.startsWith("B")) {
      const raw = StrKey.decodeClaimableBalance(json);
      return {
        type: 3,
        claimableBalanceId: {
          type: raw[0],
          v0: raw.subarray(1),
        },
      };
    }
    if (json.startsWith("L")) {
      return {
        type: 4,
        liquidityPoolId: StrKey.decodeLiquidityPool(json),
      };
    }
    if (json.startsWith("W")) {
      return { type: 5, muxedContract: muxedContractFromStrkey(json) };
    }
    throw new XdrError(`ScAddress: unsupported strkey prefix in ${json}`);
  },
});

OVERRIDES.set("ClaimableBalanceId", {
  toJson(wire) {
    const w = wire as { type: 0; v0: Uint8Array };
    // CLAIMABLE_BALANCE_ID_TYPE_V0 (=0) prefix-byte (zero-initialized)
    const raw = new Uint8Array(1 + 32);
    raw.set(w.v0, 1);
    return StrKey.encodeClaimableBalance(raw);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("ClaimableBalanceId: expected B-strkey string");
    }
    const raw = StrKey.decodeClaimableBalance(json);
    return { type: raw[0], v0: raw.subarray(1) };
  },
});

// PoolId / ContractId: typedef aliases of Hash with strkey JSON forms.
// These have distinct named schemas (emitted by the codegen) so the override
// fires only for the alias, not the underlying Hash.

OVERRIDES.set("PoolId", {
  toJson(wire) {
    return StrKey.encodeLiquidityPool(wire as Uint8Array);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("PoolId: expected L-strkey string");
    }
    return StrKey.decodeLiquidityPool(json);
  },
});

OVERRIDES.set("ContractId", {
  toJson(wire) {
    return StrKey.encodeContract(wire as Uint8Array);
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("ContractId: expected C-strkey string");
    }
    return StrKey.decodeContract(json);
  },
});

// AssetCode4 / AssetCode12: per SEP-0051, trim trailing zero bytes (down to
// a 5-byte minimum for AssetCode12 so the result is distinguishable from
// AssetCode4 output) and apply the string-escape rules.
OVERRIDES.set("AssetCode4", {
  toJson(wire) {
    return new XdrString(trimTrailingZeros(wire as Uint8Array, 0)).toJson();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("AssetCode4: expected escaped-string JSON");
    }
    return padRightZeros(XdrString.fromJson(json).bytes, 4);
  },
});

OVERRIDES.set("AssetCode12", {
  toJson(wire) {
    return new XdrString(trimTrailingZeros(wire as Uint8Array, 5)).toJson();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("AssetCode12: expected escaped-string JSON");
    }
    const bytes = XdrString.fromJson(json).bytes;
    if (bytes.length < 5) {
      throw new XdrError(
        "AssetCode12: code must be at least 5 bytes; " +
          "use AssetCode4 for shorter codes",
      );
    }
    return padRightZeros(bytes, 12);
  },
});

// `AssetCode` is a `union switch (AssetType) { …Alphanum4; …Alphanum12 }`, but
// SEP-0051 renders it as the bare code string (not a tagged union). Flatten it,
// picking the arm by code length on the way back in — matching the reference.
// (`type` is the AssetType wire value: 1 = ALPHANUM4, 2 = ALPHANUM12.)
OVERRIDES.set("AssetCode", {
  toJson(wire) {
    const w = wire as {
      assetCode4?: Uint8Array;
      assetCode12?: Uint8Array;
    };
    return w.assetCode4 !== undefined
      ? new XdrString(trimTrailingZeros(w.assetCode4, 0)).toJson()
      : new XdrString(
          trimTrailingZeros(w.assetCode12 as Uint8Array, 5),
        ).toJson();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("AssetCode: expected escaped-string JSON");
    }
    const bytes = XdrString.fromJson(json).bytes;
    return bytes.length <= 4
      ? { type: 1, assetCode4: padRightZeros(bytes, 4) }
      : { type: 2, assetCode12: padRightZeros(bytes, 12) };
  },
});

function trimTrailingZeros(bytes: Uint8Array, minLen: number): Uint8Array {
  let end = bytes.length;
  while (end > minLen && bytes[end - 1] === 0) end -= 1;
  return end === bytes.length ? bytes : bytes.slice(0, end);
}

function padRightZeros(bytes: Uint8Array, length: number): Uint8Array {
  if (bytes.length >= length) return bytes;
  const out = new Uint8Array(length);
  out.set(bytes);
  return out;
}

// Wide-int parts collapse to a single decimal string.

OVERRIDES.set("Int128Parts", {
  toJson(wire) {
    return partsTo128BigInt(wire as Int128Parts, true).toString();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("Int128Parts: expected decimal string");
    }
    assertDecimalDigitBudget(json, 128, "Int128Parts");
    assertDecimalString(json, "Int128Parts");
    const value = BigInt(json);
    assertBigIntFits(value, true, 128, "Int128Parts");
    return bigIntTo128Parts(value, true);
  },
});

OVERRIDES.set("Uint128Parts", {
  toJson(wire) {
    return partsTo128BigInt(wire as Int128Parts, false).toString();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("Uint128Parts: expected decimal string");
    }
    assertDecimalDigitBudget(json, 128, "Uint128Parts");
    assertDecimalString(json, "Uint128Parts");
    const value = BigInt(json);
    assertBigIntFits(value, false, 128, "Uint128Parts");
    return bigIntTo128Parts(value, false);
  },
});

OVERRIDES.set("Int256Parts", {
  toJson(wire) {
    return partsTo256BigInt(wire as Int256Parts, true).toString();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("Int256Parts: expected decimal string");
    }
    assertDecimalDigitBudget(json, 256, "Int256Parts");
    assertDecimalString(json, "Int256Parts");
    const value = BigInt(json);
    assertBigIntFits(value, true, 256, "Int256Parts");
    return bigIntTo256Parts(value, true);
  },
});

OVERRIDES.set("Uint256Parts", {
  toJson(wire) {
    return partsTo256BigInt(wire as Int256Parts, false).toString();
  },
  fromJson(json) {
    if (typeof json !== "string") {
      throw new XdrError("Uint256Parts: expected decimal string");
    }
    assertDecimalDigitBudget(json, 256, "Uint256Parts");
    assertDecimalString(json, "Uint256Parts");
    const value = BigInt(json);
    assertBigIntFits(value, false, 256, "Uint256Parts");
    return bigIntTo256Parts(value, false);
  },
});

function roundUp4(n: number): number {
  return n + ((4 - (n % 4)) % 4);
}
